import type { paths } from "../types/api.generated";
import * as api from "./client";

// ============================================================================
// Type Definitions
// ============================================================================

type APIRegion =
  paths["/api/v1/regions/{id}"]["get"]["responses"]["200"]["content"]["application/json"]["region"];

// UI types - map termId → id and parentId to string for component compatibility
export type Region = Omit<APIRegion, "termId" | "parentId"> & {
  id: string;
  parentId?: string | null;
};
export type RegionTreeNode = Region & { children: RegionTreeNode[] };

// Constants
const DEFAULT_TREE_DEPTH = 4;

// ============================================================================
// Type Aliases from Generated OpenAPI Types
// ============================================================================

export type RegionListResponse =
  paths["/api/v1/regions"]["get"]["responses"]["200"]["content"]["application/json"];
export type RegionSingleResponse =
  paths["/api/v1/regions/{id}"]["get"]["responses"]["200"]["content"]["application/json"];
export type RegionAncestorsResponse =
  paths["/api/v1/regions/{id}/ancestors"]["get"]["responses"]["200"]["content"]["application/json"];
export type RegionChildrenResponse =
  paths["/api/v1/regions/{id}/children"]["get"]["responses"]["200"]["content"]["application/json"];

// ============================================================================
// Helper functions
// ============================================================================

// Map termId → id and parentId to string for UI compatibility
function mapRegion(r: RegionListResponse["regions"][0]): Region {
  return {
    ...r,
    id: r.termId,
    parentId: r.parentId ?? null,
  };
}

// The OpenAPI generated type only shows children one level deep, but runtime data is recursive
type RegionWithRecursiveChildren = RegionChildrenResponse["regions"][0] & {
  children?: RegionWithRecursiveChildren[];
};

function mapRegionTree(r: RegionWithRecursiveChildren): RegionTreeNode {
  return {
    ...r,
    id: r.termId,
    parentId: r.parentId ?? null,
    children: r.children?.map(mapRegionTree) || [],
  };
}

// ============================================================================
// Service
// ============================================================================

export const regions = {
  getAll: async (): Promise<Region[]> => {
    const response = await api.get<RegionListResponse>("/v1/taxonomy/regions");
    return response.regions.map(mapRegion);
  },

  // NOTE: every function on this `regions` object below targets
  // `/v1/taxonomy/regions` — the `taxonomy.regions` UUID tree owned by the
  // staff-ui Regions admin CRUD (`src/pages/Regions/`). Do NOT repoint these
  // at region-countries; the admin page edits the UUID taxonomy directly.

  getById: async (id: string): Promise<Region> => {
    const response = await api.get<RegionSingleResponse>(`/v1/taxonomy/regions/${id}`);
    return mapRegion(response.region);
  },

  getAncestors: async (id: string): Promise<Region[]> => {
    const response = await api.get<RegionAncestorsResponse>(`/v1/taxonomy/regions/${id}/ancestors`);
    return response.ancestors.map(mapRegion);
  },

  getChildren: async (
    id: string,
    depth: number = DEFAULT_TREE_DEPTH,
  ): Promise<RegionTreeNode[]> => {
    const response = await api.get<RegionChildrenResponse>(
      `/v1/taxonomy/regions/${id}/children?depth=${depth}`,
    );
    return (response.regions as RegionWithRecursiveChildren[]).map(mapRegionTree);
  },

  create: async (data: {
    name: string;
    code: string;
    parentId?: string | null;
    displayOrder?: number;
    metadata?: unknown;
  }): Promise<Region> => {
    const response = await api.post<RegionSingleResponse>("/v1/taxonomy/regions", data);
    return mapRegion(response.region);
  },

  update: async (
    id: string,
    data: {
      name?: string;
      code?: string;
      displayOrder?: number;
      metadata?: unknown;
    },
  ): Promise<Region> => {
    const response = await api.put<RegionSingleResponse>(`/v1/taxonomy/regions/${id}`, data);
    return mapRegion(response.region);
  },

  delete: async (id: string): Promise<void> => {
    await api.del(`/v1/taxonomy/regions/${id}`);
  },

  move: async (id: string, parentId: string | null): Promise<Region> => {
    const response = await api.put<RegionSingleResponse>(`/v1/taxonomy/regions/${id}/move`, {
      parentId,
    });
    return mapRegion(response.region);
  },
};

// ============================================================================
// Profiles region source: `taxonomy.api_region_country_v2` (the WordPress
// taxonomy the syncer speaks). Served via staff-bff at
// `GET /api/v1/taxonomy/region-countries?tree=true`. This is a SEPARATE
// taxonomy from `taxonomy.regions` above — profile region pickers/forms use
// this; the Regions admin CRUD stays on `regions` above.
// Contract: specs/profiles/wordpress-regions.md §3.1.
// ============================================================================

/** Node kind discriminator. `region` covers both region-parent and region levels. */
export type RegionNodeKind = "region" | "country";

/** A single region/country node from `api_region_country_v2` (§3.1). */
export type RegionNode = {
  kind: RegionNodeKind;
  /** region_id or country_id from api_region_country_v2. */
  id: number;
  /** Nullable: the `api_region_country_v2` name columns can be null. */
  name: string | null;
  slug: string | null;
  /** Parents are always regions; top-level region-parents have null. */
  parentKind: "region" | null;
  parentId: number | null;
  displayOrder: number | null;
};

type RegionCountriesTreeResponse = { nodes: RegionNode[] };

export const regionCountries = {
  /**
   * Fetch the region/country tree for profile region pickers. Returns the flat
   * node set; consumers build the tree off `(parentKind, parentId)` and key
   * identity by `${kind}:${id}`.
   *
   * When `site` (a `legacy_publications.site` domain, e.g. `legal500.fr`) is
   * passed, the API scopes the tree to the countries that website publishes plus
   * their region ancestors (spec 19 §3.1). Omitting `site` returns the full
   * tree unchanged. `site` is appended to the query and echoed in the caller's
   * cache key so per-site trees don't collide.
   */
  getTree: async (site?: string): Promise<RegionNode[]> => {
    const query = site ? `?tree=true&site=${encodeURIComponent(site)}` : "?tree=true";
    const response = await api.get<RegionCountriesTreeResponse>(
      `/v1/taxonomy/region-countries${query}`,
    );
    return response.nodes ?? [];
  },
};
